import React, { useEffect, useState } from "react";
import API from "../api/axios";
import Navbar from "../components/Navbar";
import {
    Wallet,
    Plus,
    Minus,
    AlertCircle,
    CheckCircle,
    Loader2,
    X,
    DollarSign
} from "lucide-react";
import qrCode from '../asset/qrcode.jpeg';

export default function Dashboard() {
    const [balance, setBalance] = useState(0);
    const [amount, setAmount] = useState("");
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [modalType, setModalType] = useState(""); // 'add' or 'withdraw'
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");
    const [loading, setLoading] = useState(false);
    const [fetching, setFetching] = useState(true);
    const [status, setStatus] = useState()
    const [accountBlocked, setAccountBlocked] = useState(false);
    const [reInvestLoading, setReInvestLoading] = useState(false);
    const [showQR, setShowQR] = useState(false);
    const [referenceCode, setReferenceCode] = useState("");
    const [qrStep, setQrStep] = useState(1);


    const [walletAddress, setWalletAddress] = useState("");
    const [showWalletModal, setShowWalletModal] = useState(false);
    const [walletUpdateLoading, setWalletUpdateLoading] = useState(false);
    const [userId, setUserId] = useState("");


    useEffect(() => {
        const fetchProfile = async () => {
            setLoading(true);
            try {
                const res = await API.get("/wallet/profile");

                setStatus(res.data?.status);
                setAccountBlocked(res.data?.accountBlocked);
                setUserId(res.data?.id || res.data?._id || "");

                // ⭐ CHECK FOR WALLET ADDRESS
                if (!res.data?.walletAddress || res.data.walletAddress === "") {
                    setShowWalletModal(true);
                }

                setWalletAddress(res.data?.walletAddress || "");
            } catch (err) {
                console.error(err);
                setError("Failed to load profile.");
            } finally {
                setLoading(false);
            }
        };
        fetchProfile();
    }, []);



    const updateWalletAddress = async () => {
        if (!walletAddress.trim()) {
            setError("Wallet address is required");
            return;
        }

        setWalletUpdateLoading(true);
        setError("");

        try {
            await API.put(`/auth/update-wallet-address/${userId}`, {
                walletAddress: walletAddress.trim(),
            });


            setShowWalletModal(false);
            setSuccess("Wallet address updated successfully!");
        } catch (err) {
            setError(err.response?.data?.message || "Update failed");
        } finally {
            setWalletUpdateLoading(false);
        }
    };



    // useEffect(() => {
    //     const fetchProfile = async () => {
    //         setLoading(true);
    //         setError("");
    //         try {
    //             const res = await API.get("/wallet/profile");
    //             setStatus(res.data?.status);
    //             setAccountBlocked(res.data?.accountBlocked);
    //         } catch (err) {
    //             setError("Failed to load profile. Please try again.");
    //             console.error(err);
    //         } finally {
    //             setLoading(false);
    //         }
    //     };
    //     fetchProfile();
    // }, [])

    const fetchBalance = async () => {
        setFetching(true);
        try {
            const res = await API.get("/wallet/balance");
            setBalance(res.data.balance);
        } catch (err) {
            console.error(err);
        } finally {
            setFetching(false);
        }
    };

    const handleSubmit = async () => {
        const numAmount = parseFloat(amount);

        // Validate positive amount
        if (!numAmount || numAmount <= 0) {
            setError("Enter a valid amount");
            return;
        }

        // Deposit minimum = 25
        if (modalType === "add" && numAmount < 25) {
            setError("Minimum Deposit Amount is 25");
            return;
        }

        // Withdraw minimum = 10
        if (modalType === "withdraw" && numAmount < 10) {
            setError("Minimum Withdrawal Amount is 10");
            return;
        }

        // Withdraw balance check
        if (modalType === "withdraw" && numAmount > balance) {
            setError("Insufficient balance");
            return;
        }

        if (modalType === "add") {
            setIsModalOpen(false);
            setShowQR(true);
            setQrStep(1);
            return;
        }

        setLoading(true);
        setError("");
        setSuccess("");

        try {
            const endpoint = modalType === "add" ? "/wallet/add" : "/wallet/withdraw";
            await API.post(endpoint, {
                amount: numAmount,
                walletAddress: walletAddress
            });

            setSuccess(
                `${numAmount} ${modalType === "add" ? "added" : "withdrawn"} successfully!`
            );

            setAmount("");
            fetchBalance();

            setTimeout(() => {
                setIsModalOpen(false);
                setSuccess("");
            }, 1500);
        } catch (err) {
            setError(err.response?.data?.error || "Something went wrong");
        } finally {
            setLoading(false);
        }
    };


    const openModal = (type) => {
        setModalType(type);
        setAmount("");
        setError("");
        setSuccess("");

        if (type === "add") {
            setIsModalOpen(true);
        } else {
            setIsModalOpen(true);
        }
    };


    useEffect(() => {
        fetchBalance();
    }, []);


    const submitRefrencePayment = async () => {
        if (!referenceCode.trim()) {
            setError("Reference Code is required");
            return;
        }

        setLoading(true);
        setError("");

        try {
            await API.post("/wallet/add", {
                amount: parseFloat(amount),
                referenceCode: referenceCode.trim(),
            });

            setShowQR(false);
            setSuccess(`₹${amount} added successfully!`);

            fetchBalance();
            setReferenceCode("");
        } catch (err) {
            setError(err.response?.data?.error || "Payment verification failed");
        } finally {
            setLoading(false);
        }
    };


    return (
        <>
            <div className="min-h-screen bg-gray-50 p-4 md:p-8">
                <div className="max-w-4xl mx-auto">

                    {/* Balance Card */}
                    <div className="bg-white rounded-3xl shadow-2xl p-8 md:p-12 text-center border border-gray-200">
                        {fetching ? (
                            <div className="flex justify-center">
                                <Loader2 className="w-12 h-12 animate-spin text-gray-700" />
                            </div>
                        ) : (
                            <>
                                <div className="flex justify-center mb-6">
                                    <div className="p-5 bg-gray-800 rounded-full shadow-inner">
                                        <Wallet className="w-14 h-14 text-white" />
                                    </div>
                                </div>
                                <p className="text-gray-600 text-base font-medium">Wallet Balance</p>
                                <h1 className="text-6xl font-bold text-gray-800 mt-4 flex items-center justify-center gap-2">
                                    <DollarSign className="w-12 h-12 text-gray-800" />
                                    {balance.toLocaleString()}
                                </h1>

                                {/* ⭐ REINVEST BUTTON ⭐ */}
                                <div className="mt-6 flex justify-center">
                                    <button
                                        disabled={balance <= 0 || !status || reInvestLoading}
                                        onClick={() => {
                                            setModalType("add");
                                            setAmount(balance);
                                            setShowQR(true);
                                            setQrStep(1);
                                        }}

                                        className={`px-6 py-3 rounded-2xl font-semibold text-white text-lg shadow flex items-center gap-3
                                        ${balance <= 0 || !status || reInvestLoading
                                                ? "bg-gray-400 cursor-not-allowed"
                                                : "bg-blue-600 hover:bg-blue-700 hover:shadow-xl transition"
                                            }`}
                                    >
                                        {reInvestLoading ? (
                                            <Loader2 className="w-6 h-6 animate-spin text-white" />
                                        ) : (
                                            "Re-Investment"
                                        )}
                                    </button>
                                </div>



                            </>
                        )}
                    </div>

                    {/* Action Buttons */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mt-10">

                        {/* Add Money Button */}
                        <button
                            disabled={!status}
                            onClick={() => openModal("add")}
                            className={`group flex items-center justify-center space-x-3 px-8 py-6 rounded-2xl shadow-lg transform transition-all duration-300
                             ${!status
                                    ? "bg-gray-300 cursor-not-allowed"
                                    : "bg-gradient-to-r from-emerald-600 to-emerald-700 hover:shadow-xl hover:scale-105"
                                }`}
                        >
                            <Plus
                                className={`w-8 h-8 transition-transform group-hover:rotate-90 ${!status
                                    ? "text-yellow-600"

                                    : "text-white"
                                    }`}
                            />
                            <span
                                className={`text-xl font-semibold ${!status
                                    ? "text-yellow-600"

                                    : "text-white"
                                    }`}
                            >
                                {!status
                                    ? "Add Money (User Not Active)"

                                    : "Add Money"}
                            </span>
                        </button>

                        {/* Withdraw Button */}
                        <button
                            onClick={() => openModal("withdraw")}
                            disabled={!status || balance === 0}
                            className={`group flex items-center justify-center space-x-3 px-8 py-6 rounded-2xl shadow-lg transform transition-all duration-300
                            ${!status || balance === 0
                                    ? "bg-gray-300 cursor-not-allowed"
                                    : "bg-gradient-to-r from-red-600 to-rose-700 hover:shadow-xl hover:scale-105"
                                }`}
                        >
                            <Minus
                                className={`w-8 h-8 transition-transform group-hover:-rotate-90 ${!status ? "text-yellow-600" : "text-white"
                                    }`}
                            />
                            <span className={`text-xl font-semibold ${!status ? "text-yellow-600" : "text-white"}`}>
                                {!status
                                    ? "Withdraw (User Not Active)"
                                    : accountBlocked
                                        ? "Withdraw (Full Allowed)"
                                        : "Withdraw (Profit Only)"}
                            </span>
                        </button>

                    </div>


                    {/* Account Blocked Message */}
                    {accountBlocked && (
                        <div className="mt-8 bg-red-50 border border-red-200 text-red-700 px-8 py-6 rounded-3xl shadow-lg text-center">
                            <h3 className="text-xl font-bold flex items-center justify-center gap-3">
                                <AlertCircle className="w-7 h-7" />
                                Account Blocked
                            </h3>
                            Your account is temporarily blocked because your wallet balance reached 2× your total deposits.
                            You will not receive ROI or Commission until you deposit again.
                            Deposit again to reactivate your earnings.

                        </div>
                    )}

                </div>
            </div>

            {/* Modal */}
            {/* Modal */}
            {isModalOpen && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50">
                    <div className="bg-white rounded-3xl shadow-2xl w-full max-w-md p-8 relative">

                        {/* Close Button */}
                        <button
                            onClick={() => setIsModalOpen(false)}
                            className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-full p-2 transition"
                        >
                            <X className="w-6 h-6" />
                        </button>

                        <div className="text-center mb-8">
                            <div className="inline-flex p-4 bg-gray-100 rounded-full mb-5">
                                {modalType === "add" ? (
                                    <Plus className="w-10 h-10 text-emerald-600" />
                                ) : (
                                    <Minus className="w-10 h-10 text-red-600" />
                                )}
                            </div>

                            <h2 className="text-2xl font-bold text-gray-800">
                                {modalType === "add" ? "Add Money" : "Withdraw Money"}
                            </h2>

                            <p className="text-gray-600 mt-2 flex items-center justify-center gap-1 text-lg">
                                Current Balance:{" "}
                                <span className="font-bold flex items-center gap-1 text-xl">
                                    <DollarSign className="w-6 h-6 text-gray-800" /> {balance.toLocaleString()}
                                </span>
                            </p>

                            {/* ⭐ ADDED NOTE HERE ⭐ */}
                            <p className="text-sm text-gray-500 mt-3">
                                {modalType === "add"
                                    ? "Minimum deposit amount is 25"
                                    : "Minimum withdrawal amount is 10"}
                            </p>
                        </div>

                        <div className="space-y-6">
                            <div>
                                <label className="block text-base font-medium text-gray-700 mb-3 flex items-center gap-2">
                                    Amount <DollarSign className="w-6 h-6 text-gray-800" />
                                </label>
                                <input
                                    type="number"
                                    value={amount}
                                    onChange={(e) => setAmount(e.target.value)}
                                    className="w-full px-5 py-4 border border-gray-300 rounded-2xl focus:ring-2 focus:ring-gray-600 focus:border-transparent outline-none transition text-lg font-medium text-gray-800"
                                    placeholder="0.00"
                                    min="1"
                                    step="0.01"
                                />
                            </div>

                            {/* Error */}
                            {error && (
                                <div className="flex items-center space-x-3 text-red-700 bg-red-50 px-6 py-4 rounded-2xl border border-red-200">
                                    <AlertCircle className="w-6 h-6 flex-shrink-0" />
                                    <span className="font-medium">{error}</span>
                                </div>
                            )}

                            {/* Success */}
                            {success && (
                                <div className="flex items-center space-x-3 text-emerald-700 bg-emerald-50 px-6 py-4 rounded-2xl border border-emerald-200">
                                    <CheckCircle className="w-6 h-6 flex-shrink-0" />
                                    <span className="font-medium flex items-center gap-1">
                                        <DollarSign className="w-5 h-5 text-emerald-700" /> {success}
                                    </span>
                                </div>
                            )}

                                          {modalType === "withdraw" && amount && parseFloat(amount) > 0 && (
                                <div className="flex items-center space-x-3 text-blue-700 bg-blue-50 px-6 py-4 rounded-2xl border border-blue-200 mt-2">
                                    <AlertCircle className="w-6 h-6 flex-shrink-0" />
                                    <span className="font-medium">
                                        You will receive ₹
                                        {(amount - amount * 0.05).toFixed(2)} after 5% deduction.
                                    </span>
                                </div>
                            )}

                            {/* Submit Button */}
                            <button
                                onClick={handleSubmit}
                                disabled={loading || !amount}
                                className={`w-full py-5 rounded-2xl font-bold text-white transition-all duration-200 flex items-center justify-center space-x-3 text-lg
                        ${loading || !amount
                                        ? "bg-gray-400 cursor-not-allowed"
                                        : modalType === "add"
                                            ? "bg-gradient-to-r from-emerald-600 to-emerald-700 hover:shadow-xl"
                                            : "bg-gradient-to-r from-red-600 to-rose-700 hover:shadow-xl"
                                    }`}
                            >
                                {loading ? (
                                    <Loader2 className="w-7 h-7 animate-spin" />
                                ) : (
                                    <>
                                        <span>{modalType === "add" ? "Add" : "Withdraw"}</span>
                                        <span className="flex items-center gap-1">
                                            <DollarSign className="w-6 h-6" /> {amount || "0"}
                                        </span>
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ⭐ QR CODE PAYMENT MODAL ⭐ */}
            {showQR && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50">

                    {/* Scrollable Box */}
                    <div className="bg-white rounded-3xl shadow-2xl w-full max-w-xl md:max-w-3xl p-8 relative max-h-[90vh] overflow-y-auto">

                        <button
                            onClick={() => setShowQR(false)}
                            className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-full p-2 transition"
                        >
                            <X className="w-6 h-6" />
                        </button>


                        {/* MAIN WRAPPER */}
                        <div className="flex flex-col md:flex-row md:gap-10 gap-6 mt-6">

                            {/* LEFT QR SECTION */}
                            <div className="flex flex-col items-center justify-start md:w-1/2 text-center">

                                <h2 className="text-2xl font-bold text-gray-900 mb-4">
                                    Scan & Pay
                                </h2>

                                <img
                                    src={qrCode}
                                    alt="QR"
                                    className="w-52 h-52 md:w-60 md:h-60 rounded-2xl shadow-xl mb-4"
                                />

                                <p className="text-lg font-semibold text-gray-700">
                                    Amount: <span className="text-2xl font-bold">${amount}</span>
                                </p>


                               
                                {/* Wallet Address Box */}
                                <div className="mt-5 w-full text-center">

                                    <p className="text-gray-800 font-semibold text-lg">Wallet adress BEP20</p>

                                    <div className="flex items-center justify-between gap-2 mt-3 bg-gray-100 px-4 py-3 rounded-2xl shadow-inner border border-gray-200">

                                        <span className="text-sm font-mono break-all text-gray-700">
                                            0x787D6e6b599484a43adD89b00A66f191464fDa21
                                        </span>

                                        <button
                                            onClick={() => {
                                                navigator.clipboard.writeText("0x787D6e6b599484a43adD89b00A66f191464fDa21");

                                                // Show copied text for 2 seconds
                                                const copiedText = document.getElementById("copiedText");
                                                copiedText.style.opacity = 1;
                                                setTimeout(() => {
                                                    copiedText.style.opacity = 0;
                                                }, 1500);
                                            }}
                                            className="bg-blue-600 text-white px-3 py-2 rounded-xl text-sm font-medium hover:bg-blue-700 transition"
                                        >
                                            Copy
                                        </button>
                                    </div>

                                    {/* Copied Text Animation */}
                                    <p
                                        id="copiedText"
                                        className="text-green-600 font-semibold mt-2 text-sm transition-opacity duration-300 opacity-0"
                                    >
                                        Copied!
                                    </p>
                                </div>

                            </div>


                            {/* RIGHT SECTION */}
                            <div className="md:w-1/2">

                                {/* Step Guide */}
                                {qrStep === 1 && (
                                    <>
                                        <h2 className="text-xl font-bold text-gray-900 mb-4">
                                            Complete the Payment
                                        </h2>

                                        <div className="space-y-3 mb-6">

                                            {[
                                                "Open any payment app.",
                                                "Tap on Scan QR option.",
                                                "Scan the QR code shown above.",
                                                `Pay the exact amount: $${amount}`
                                            ].map((text, index) => (
                                                <div
                                                    key={index}
                                                    className="flex gap-4 bg-gray-50 p-3 rounded-xl shadow-sm"
                                                >
                                                    <div className="w-8 h-8 flex items-center justify-center rounded-full bg-blue-100 text-blue-700 font-bold">
                                                        {index + 1}
                                                    </div>
                                                    <p className="text-gray-700">{text}</p>
                                                </div>
                                            ))}
                                        </div>

                                        <button
                                            onClick={() => setQrStep(2)}
                                            className="w-full py-4 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-2xl shadow-lg transition"
                                        >
                                            I Have Paid
                                        </button>
                                    </>
                                )}

                                {/* Reference Code Input */}
                                {qrStep === 2 && (
                                    <>
                                        <h2 className="text-xl font-bold text-gray-900 mb-4">
                                            Verify Your Payment
                                        </h2>

                                        <p className="text-gray-600 mb-3">
                                            Enter the <b>Reference Number</b> from your payment receipt.
                                        </p>

                                        <input
                                            type="text"
                                            value={referenceCode}
                                            onChange={(e) => setReferenceCode(e.target.value)}
                                            placeholder="Enter Reference Code"
                                            className="w-full px-5 py-4 border border-gray-300 rounded-2xl text-lg focus:ring-2 focus:ring-blue-500 outline-none"
                                        />

                                        {error && (
                                            <p className="text-red-600 mt-2">{error}</p>
                                        )}

                                        <button
                                            onClick={submitRefrencePayment}
                                            disabled={loading}
                                            className="w-full py-4 mt-6 bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-2xl shadow-lg transition flex justify-center"
                                        >
                                            {loading ? (
                                                <Loader2 className="w-6 h-6 animate-spin" />
                                            ) : (
                                                "Submit & Add Balance"
                                            )}
                                        </button>

                                        <button
                                            onClick={() => setQrStep(1)}
                                            className="w-full mt-3 text-gray-600 hover:text-gray-800 text-sm font-medium"
                                        >
                                            ← Go Back
                                        </button>
                                    </>
                                )}

                            </div>

                        </div>
                    </div>
                </div>
            )}


            {showWalletModal && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50">
                    <div className="bg-white rounded-3xl shadow-2xl w-full max-w-md p-8 relative">

                        <button
                            onClick={() => { }}
                            className="absolute top-4 right-4 text-gray-400 bg-gray-100 rounded-full p-2 cursor-not-allowed"
                            title="You must update wallet address"
                        >
                            <X className="w-6 h-6" />
                        </button>

                        <h2 className="text-2xl font-bold text-gray-800 text-center mb-4">
                            Update Wallet Address
                        </h2>
                        <p className="text-center text-gray-600 mb-6">
                            Please enter your wallet address to continue using your account.
                        </p>

                        <input
                            type="text"
                            value={walletAddress}
                            onChange={(e) => setWalletAddress(e.target.value)}
                            placeholder="Enter your wallet address"
                            className="w-full px-5 py-4 border border-gray-300 rounded-2xl text-lg focus:ring-2 focus:ring-blue-600 outline-none"
                        />

                        {error && <p className="text-red-600 mt-2">{error}</p>}

                        <button
                            onClick={updateWalletAddress}
                            disabled={walletUpdateLoading}
                            className="w-full py-4 mt-6 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-2xl shadow-lg flex justify-center"
                        >
                            {walletUpdateLoading ? (
                                <Loader2 className="w-6 h-6 animate-spin" />
                            ) : (
                                "Save Wallet Address"
                            )}
                        </button>
                    </div>
                </div>
            )}


        </>
    );
}